export const BACKGROUND_COLORS = {
  one: "#06060C",
  two: "rgba(255, 255, 255, 0.08)",
  three: "#5e5d6a",
  four: "rgba(255,255,255,0.04)",
  five: "rgba(6, 6, 12, 1)",
  six: "rgba(21, 21, 27, 0.72)",
  seven: "#0D0D13",
  eight: "#16161C",
  nine: "rgba(21, 21, 27, 0.4)",
  twelve: "#1B1B20",
  thirteen: "rgba(6, 6, 12, 0.72)",
  fourteen: "rgba(255,255,255,0.03)",
  fifteen: "rgba(255,255,255,0.02)",

  tooltip: "rgba(21, 21, 27, 0.96)",

  // Components bg color
  component: "#0E0E14",
} as const;

export const TEXT_COLORS = {
  one: "#ffffff",
  two: "#A5A5A8",
  three: "#6A6A6D",
  four: "#424246",
} as const;

export const ICON_COLORS = {
  one: "#ffffff",
  two: "#A5A5A8",
  three: "#6A6A6D",
  four: "#424246",
};

export const LAYER_COLORS = {
  one: "#12121A",
  two: "#18181E",
  three: "#1E1E24",
  four: "#24242A",
  highlightDark: "#090E15",
  highlightLight: "#0E1F28",
};

export const FIELD_COLORS = {
  one: "#1E1E24",
  two: "#24242A",
};

// Primary-action color. Per DESIGN.md the sole brand accent is cyan #4CD7F4
// ("Primary brand color, CTAs, links" — the decision log deliberately rejected
// purple), so this aliases the `COLORS.highlight` cyan scale (identical alpha
// levels). Kept as a named "primary action" token so every consumer flips at
// once. `on` is the WCAG-safe dark text/icon color for solid `.one` fills
// (bright cyan fails contrast with white — pairs with `COLORS.highlight.on`).
export const PRIMARY_COLORS = {
  one: "#4CD7F4",
  two: "rgba(76, 215, 244, 0.40)",
  three: "rgba(76, 215, 244, 0.24)",
  four: "rgba(76, 215, 244, 0.12)",
  five: "rgba(76, 215, 244, 0.08)",
  six: "rgba(76, 215, 244, 0.04)",
  on: "#06060C",
} as const;

export const NEGATIVE_COLORS = {
  one: "rgba(252, 10, 82, 1)",
  two: "rgba(252, 10, 82, 0.40)",
  three: "rgba(252, 10, 82, 0.24)",
  four: "rgba(252, 10, 82, 0.12)",
  five: "rgba(252, 10, 82, 0.08)",
  six: "rgba(252, 10, 82, 0.04)",
} as const;

export const COLORS = {
  positive: {
    one: "rgba(0, 225, 0, 1)",
    two: "rgba(0, 225, 0, 0.40)",
    three: "rgba(0, 225, 0, 0.24)",
    four: "rgba(0, 225, 0, 0.12)",
    five: "rgba(0, 225, 0, 0.08)",
    six: "rgba(0, 225, 0, 0.04)",
  },
  negative: {
    one: "rgba(252, 10, 82, 1)",
    two: "rgba(252, 10, 84, 0.40)",
    three: "rgba(252, 10, 84, 0.24)",
    four: "rgba(252, 10, 84, 0.12)",
    five: "rgba(252, 10, 84, 0.08)",
    six: "rgba(252, 10, 84, 0.04)",
  },
  // Price DIRECTION colors (up = gain, down = loss). User-configurable via the
  // `--color-up` / `--color-down` CSS vars (written by applyDirectionColors from
  // utils/directionColors.ts). Because these are `rgb(var(...) / α)` strings the
  // DOM re-themes live when the scheme changes — no re-render. Fallbacks = the
  // `classic` preset (green up / red down). Use these for candles, PnL,
  // price-change, buy/sell and bid/ask — NOT for status (positive/negative).
  up: {
    one: "rgb(var(--color-up, 22 199 132) / 1)",
    two: "rgb(var(--color-up, 22 199 132) / 0.40)",
    three: "rgb(var(--color-up, 22 199 132) / 0.24)",
    four: "rgb(var(--color-up, 22 199 132) / 0.12)",
    five: "rgb(var(--color-up, 22 199 132) / 0.08)",
    six: "rgb(var(--color-up, 22 199 132) / 0.04)",
    // Readable text/icon color to place ON a solid `up.one` fill. Tracks the
    // active direction scheme via `--color-up-on` (set by applyDirectionColors
    // from the up color's luminance): dark text on bright ups (classic green,
    // colorblind blue), white on dark ups (eastAsian red). Fallback = classic
    // (green → dark). Every solid buy/long button MUST use this, never a
    // hardcoded white/black — a constant fails on schemes with different
    // brightness (e.g. colorblind's orange down at ~2.2:1 white).
    on: "rgb(var(--color-up-on, 6 6 12))",
  },
  down: {
    one: "rgb(var(--color-down, 234 57 67) / 1)",
    two: "rgb(var(--color-down, 234 57 67) / 0.40)",
    three: "rgb(var(--color-down, 234 57 67) / 0.24)",
    four: "rgb(var(--color-down, 234 57 67) / 0.12)",
    five: "rgb(var(--color-down, 234 57 67) / 0.08)",
    six: "rgb(var(--color-down, 234 57 67) / 0.04)",
    // Readable text/icon color to place ON a solid `down.one` fill. Tracks the
    // active direction scheme via `--color-down-on`: white on dark downs
    // (classic red), dark on bright downs (colorblind orange, eastAsian green).
    // Fallback = classic (red → white). Pairs with `up.on`.
    on: "rgb(var(--color-down-on, 255 255 255))",
  },
  system: {
    one: "rgba(255, 122, 0, 1)",
    two: "rgba(255, 122, 0, 0.64)",
    three: "rgba(255, 122, 0, 0.24)",
    four: "rgba(255, 122, 0, 0.12)",
    five: "rgba(255, 122, 0, 0.08)",
    six: "rgba(255, 122, 0, 0.04)",
  },
  // TODO: - Update blue
  blue: {
    one: "rgba(76, 215, 244, 1)",
    two: "rgba(76, 215, 244, 0.08)",
    // two, but solid. TODO: - Confirm with vp
    twoSolid: "#0D171F",
    three: "rgba(76, 215, 244, 0.12)",
    four: "rgba(76, 215, 244, 0.03)",
    five: "rgba(76, 215, 244, 0.04)",
    six: "rgba(76, 215, 244, 0.04)",
    // Readable text/icon color to place ON a solid `blue.one` fill. This cyan
    // is bright — white text fails WCAG (~1.7:1). Use dark text. See `up.on`.
    on: "#06060C",
  },
  highlight: {
    one: "#4CD7F4",
    two: "rgba(76, 215, 244, 0.40)",
    three: "rgba(76, 215, 244, 0.24)",
    four: "rgba(76, 215, 244, 0.12)",
    five: "rgba(76, 215, 244, 0.08)",
    six: "rgba(76, 215, 244, 0.04)",
    // Readable text/icon color to place ON a solid `highlight.one` fill (bright
    // cyan — white fails contrast). Use dark text. Pairs with `up.on`/`blue.on`.
    on: "#06060C",
  },
  // Muted purple used for inline hyperlinks (e.g. "View transaction" tx-explorer
  // links). Distinct from the brand purple `PRIMARY_COLORS.one` (#4600FF).
  link: {
    one: "#6C5DD3",
  },
  white: {
    one: "#fff",
    two: "rgba(255, 255, 255, 0.08)",
    three: "rgba(255, 255, 255, 0.16)",
    four: "rgba(255, 255, 255, 0.24)",
    five: "rgba(255, 255, 255, 0.04)",
  },
  neutral: {
    one: "#FFFFFF",
    two: "rgba(255, 255, 255, 0.40)",
    three: "rgba(255, 255, 255, 0.24)",
    four: "rgba(255, 255, 255, 0.12)",
    five: "rgba(255, 255, 255, 0.08)",
    six: "rgba(255, 255, 255, 0.04)",
  },
  black: {
    one: "rgba(0,0,0,1)",
    two: "rgba(0,0,0,0.72)",
    three: "rgba(22, 22, 28, 1)",
    four: "rgba(0,0,0,0.24)",
    five: "rgba(17, 17, 19, 1)",
  },
  background: {
    one: "#06060C",
  },
} as const;

export const BORDER_COLORS = {
  one: "#15151B",
  two: "#1A1A1F",
  three: "#2B2B30",
  four: "#A5A5A8",
  five: "#FFFFFF",
} as const;

export const MODAL_COLORS = {
  one: "rgba(14, 14, 20, 0.92)",
};

export const ASSET_COLORS = {
  usdc: {
    one: "rgba(62, 115, 196, 1)",
    two: "rgba(62, 115, 196, 0.64)",
    three: "rgba(62, 115, 196, 0.24)",
    four: "rgba(62, 115, 196, 0.12)",
  },
};
